import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Search, Eye, Download, RefreshCw, Trash2, Upload, FileText, AlertCircle, CheckCircle, Clock, XCircle, Filter, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import DocumentUploadsService from "@/api/documentUploads.service";
import DocumentTypesService from "@/api/documentTypes.service";
import { DocumentUpload } from "@/components/DocumentUpload";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableHeader,
  TableRow,
  TableHead,
} from "@/components/ui/table";

interface DocumentUploadRecord {
  id: number;
  publicId: string;
  businessId: number;
  documentTypeId?: number;
  fileName: string;
  originalFileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  uploadMethod: 'web' | 'mobile' | 'email' | 'api';
  uploadedBy: number;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  dateCreated: string;
  documentType?: {
    id: number;
    name: string;
  };
  extraction?: {
    confidenceScore?: number;
    extractionStatus: 'pending' | 'completed' | 'failed';
    structuredData?: any;
    errorMessage?: string;
  };
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

export default function DocumentUploads() {
  const [documents, setDocuments] = useState<DocumentUploadRecord[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [uploadMethodFilter, setUploadMethodFilter] = useState<string>("all");
  const [selectedDocument, setSelectedDocument] = useState<DocumentUploadRecord | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
      fetchDocumentTypes();
    }
  }, [selectedBusiness]);

  const fetchData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await DocumentUploadsService.getBusinessDocuments(selectedBusiness.id);
      if (response.data.result === "success") {
        setDocuments(response.data.documents || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch document uploads",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await DocumentTypesService.getDocumentTypes();
      if (response.data.result === "success") {
        setDocumentTypes(response.data.documentTypes || []);
      }
    } catch (error) {
      console.error("Failed to fetch document types:", error);
    }
  };

  const handleReprocess = async (documentId: number) => {
    try {
      await DocumentUploadsService.reprocessDocument(documentId);
      toast({
        title: "Success",
        description: "Document reprocessing initiated",
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reprocess document",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      await DocumentUploadsService.deleteDocumentUpload(selectedDocument.id);
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      setSelectedDocument(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const handleBulkReprocess = async () => {
    if (selectedDocuments.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await DocumentUploadsService.bulkReprocessDocuments(selectedDocuments);
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: `${selectedDocuments.length} documents queued for reprocessing`,
        });
        setSelectedDocuments([]);
        fetchData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reprocess documents",
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await DocumentUploadsService.bulkDeleteDocuments(selectedDocuments);
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: `${selectedDocuments.length} documents deleted`,
        });
        setSelectedDocuments([]);
        fetchData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete documents",
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedDocuments.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await DocumentUploadsService.bulkUpdateStatus(selectedDocuments, newStatus);
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: `Status updated for ${selectedDocuments.length} documents`,
        });
        setSelectedDocuments([]);
        fetchData();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update document status",
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkExport = async (format: 'csv' | 'excel' | 'pdf') => {
    if (selectedDocuments.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await DocumentUploadsService.bulkExportDocuments(selectedDocuments, format);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 
              format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
              'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: `Exported ${selectedDocuments.length} documents`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export documents",
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  const handleSelectDocument = (documentId: number) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const openViewDialog = (document: DocumentUploadRecord) => {
    setSelectedDocument(document);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (document: DocumentUploadRecord) => {
    setSelectedDocument(document);
    setIsDeleteDialogOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: "bg-green-50 text-green-700 border-green-200",
      processing: "bg-blue-50 text-blue-700 border-blue-200",
      failed: "bg-red-50 text-red-700 border-red-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200"
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {getStatusIcon(status)}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.originalFileName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || doc.processingStatus === statusFilter;
    const matchesType = typeFilter === "all" || doc.documentTypeId?.toString() === typeFilter;
    const matchesMethod = uploadMethodFilter === "all" || doc.uploadMethod === uploadMethodFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesMethod;
  });

  const columns = [
    {
      id: "select",
      header: ({ table }: any) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            if (value) {
              setSelectedDocuments(filteredDocuments.map(doc => doc.id));
            } else {
              setSelectedDocuments([]);
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }: any) => (
        <Checkbox
          checked={selectedDocuments.includes(row.original.id)}
          onCheckedChange={(value) => {
            if (value) {
              setSelectedDocuments(prev => [...prev, row.original.id]);
            } else {
              setSelectedDocuments(prev => prev.filter(id => id !== row.original.id));
            }
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "fileName",
      header: "File",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{row.getValue("fileName")}</div>
            <div className="text-sm text-muted-foreground">
              {row.original.originalFileName}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "documentType",
      header: "Type",
      cell: ({ row }: any) => (
        <Badge variant="outline">
          {row.original.documentType?.name || 'Unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: "processingStatus",
      header: "Status",
      cell: ({ row }: any) => getStatusBadge(row.getValue("processingStatus")),
    },
    {
      accessorKey: "uploadMethod",
      header: "Method",
      cell: ({ row }: any) => (
        <Badge variant="secondary" className="capitalize">
          {row.getValue("uploadMethod")}
        </Badge>
      ),
    },
    {
      accessorKey: "fileSize",
      header: "Size",
      cell: ({ row }: any) => formatFileSize(row.getValue("fileSize")),
    },
    {
      accessorKey: "dateCreated",
      header: "Uploaded",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openViewDialog(row.original)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.processingStatus === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReprocess(row.original.id)}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteDialog(row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const statusCounts = {
    all: documents.length,
    pending: documents.filter(d => d.processingStatus === 'pending').length,
    processing: documents.filter(d => d.processingStatus === 'processing').length,
    completed: documents.filter(d => d.processingStatus === 'completed').length,
    failed: documents.filter(d => d.processingStatus === 'failed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Document Uploads</h1>
          <p className="text-muted-foreground">
            Manage and monitor your uploaded documents and their processing status
          </p>
        </div>
        <Button onClick={() => setIsUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <Card key={status} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setStatusFilter(status)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground capitalize">
                    {status === 'all' ? 'Total' : status}
                  </p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                {getStatusIcon(status)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by filename..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="status-filter">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="type-filter">Document Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="method-filter">Upload Method</Label>
              <Select value={uploadMethodFilter} onValueChange={setUploadMethodFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="web">Web</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedDocuments.length > 0 && (
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="px-3 py-1">
                  {selectedDocuments.length} selected
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDocuments([])}
                >
                  Clear Selection
                </Button>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                      {bulkActionLoading ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Bulk Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleBulkReprocess}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reprocess All
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Edit className="h-4 w-4 mr-2" />
                        Update Status
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('pending')}>
                          Set to Pending
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('processing')}>
                          Set to Processing
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('completed')}>
                          Set to Completed
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkStatusUpdate('failed')}>
                          Set to Failed
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Download className="h-4 w-4 mr-2" />
                        Export
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => handleBulkExport('csv')}>
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkExport('excel')}>
                          Export as Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkExport('pdf')}>
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleBulkDelete}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Documents ({filteredDocuments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredDocuments}
              section="document-uploads"
              isLoading={loading}
            />
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload New Document</DialogTitle>
            <DialogDescription>
              Upload a document for AI processing and data extraction
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DocumentUpload
              documentType="invoice"
              businessId={selectedBusiness?.id || 1}
              onUploadComplete={() => {
                setIsUploadDialogOpen(false);
                fetchData();
              }}
              onExtractionComplete={(data) => {
                console.log('Extraction completed:', data);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
            <DialogDescription>
              View document information and processing results
            </DialogDescription>
          </DialogHeader>
          {selectedDocument && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="extraction">Extraction</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>File Name</Label>
                    <p className="text-sm">{selectedDocument.fileName}</p>
                  </div>
                  <div>
                    <Label>Original Name</Label>
                    <p className="text-sm">{selectedDocument.originalFileName}</p>
                  </div>
                  <div>
                    <Label>Document Type</Label>
                    <p className="text-sm">{selectedDocument.documentType?.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label>File Size</Label>
                    <p className="text-sm">{formatFileSize(selectedDocument.fileSize)}</p>
                  </div>
                  <div>
                    <Label>Upload Method</Label>
                    <p className="text-sm capitalize">{selectedDocument.uploadMethod}</p>
                  </div>
                  <div>
                    <Label>Processing Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedDocument.processingStatus)}</div>
                  </div>
                  <div>
                    <Label>Upload Date</Label>
                    <p className="text-sm">{formatDate(selectedDocument.dateCreated)}</p>
                  </div>
                  <div>
                    <Label>MIME Type</Label>
                    <p className="text-sm">{selectedDocument.mimeType || 'Unknown'}</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="extraction" className="space-y-4">
                {selectedDocument.extraction ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Confidence Score</Label>
                      <div className="mt-2">
                        <Progress 
                          value={(selectedDocument.extraction.confidenceScore || 0) * 100} 
                          className="w-full"
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          {((selectedDocument.extraction.confidenceScore || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label>Extraction Status</Label>
                      <div className="mt-1">{getStatusBadge(selectedDocument.extraction.extractionStatus)}</div>
                    </div>
                    {selectedDocument.extraction.structuredData && (
                      <div>
                        <Label>Extracted Data</Label>
                        <pre className="mt-2 p-4 bg-muted rounded-md text-sm overflow-auto">
                          {JSON.stringify(selectedDocument.extraction.structuredData, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedDocument.extraction.errorMessage && (
                      <div>
                        <Label>Error Message</Label>
                        <p className="text-sm text-red-600 mt-1">
                          {selectedDocument.extraction.errorMessage}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No extraction data available</p>
                )}
              </TabsContent>
              
              <TabsContent value="preview" className="space-y-4">
                <div className="border rounded-lg p-4 bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-2">File Preview</p>
                  {selectedDocument.mimeType?.startsWith('image/') ? (
                    <img 
                      src={selectedDocument.fileUrl} 
                      alt={selectedDocument.fileName}
                      className="max-w-full h-auto rounded-md"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-32 border-2 border-dashed border-muted-foreground/25 rounded-md">
                      <div className="text-center">
                        <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Preview not available for this file type
                        </p>
                        <Button variant="outline" size="sm" className="mt-2" asChild>
                          <a href={selectedDocument.fileUrl} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4 mr-2" />
                            Download File
                          </a>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDocument?.fileName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 